/**
 * ============================================================================
 * DIALOGS STORE - DELETE OPERATIONS
 * ============================================================================
 * 
 * Handles deleting dialogs via the backend API.
 * Automatically deprecates related translation strings.
 * 
 * @module dialogs/api/delete
 */

import { apiService } from "@/lib/api-functions/api-service";
import { dialogStore } from "../store";
import { collectStringIdsFromDialog } from "../utils/translation";
import type { ToastFunction } from "../types";

// ============================================================================
// DELETE OPERATIONS
// ============================================================================

/**
 * Delete a dialog via API and sync the store.
 * 
 * Process:
 * 1. Collect all string IDs from the dialog
 * 2. Delete the dialog via API
 * 3. Mark related strings as deprecated
 * 4. Remove from local store
 * 
 * @param id - The dialog ID to delete
 * @param gameId - The game ID
 * @param toast - Optional toast function for notifications
 * 
 * @example
 * ```ts
 * await deleteDialog('dialog-123', 'game-456', toast);
 * ```
 */
export async function deleteDialog(
  id: string,
  gameId: string,
  toast?: ToastFunction
): Promise<void> {
  try {
    // Get the dialog data before deletion to find related strings
    const currentData = dialogStore.getData();
    const dialogToDelete = currentData.find(dialog => dialog.id === id);
    
    if (!dialogToDelete) {
      throw new Error('Dialog not found in store');
    }

    // Collect all string IDs that need to be marked as deprecated
    const stringIdsToDeprecate = collectStringIdsFromDialog(dialogToDelete.data);

    // Validate authentication
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // ======================================================================
    // DELETE VIA API
    // ======================================================================
    const result = await apiService.deleteDialog(id, gameId);

    if (!result.success) {
      throw new Error(result.error || 'Failed to delete dialog');
    }

    // ======================================================================
    // DEPRECATE RELATED STRINGS
    // ======================================================================
    if (stringIdsToDeprecate.length > 0) {
      try {
        const { markStringsAsDeprecated } = await import('../../stringsStore');
        await markStringsAsDeprecated(stringIdsToDeprecate, gameId, toast);
        
        console.log(`[deleteDialog] Deprecated ${stringIdsToDeprecate.length} related strings`);
        
        if (toast) {
          toast({
            title: "Success",
            description: `Dialog deleted and ${stringIdsToDeprecate.length} related strings marked as deprecated`,
          });
        }
      } catch (deprecationError) {
        console.error('[deleteDialog] Failed to mark strings as deprecated:', deprecationError);
        if (toast) {
          toast({
            title: "Warning",
            description: "Dialog deleted but failed to mark related strings as deprecated",
            variant: "destructive",
          });
        }
      }
    } else if (toast) {
      toast({
        title: "Success",
        description: result.data?.message || "Dialog deleted successfully",
      });
    }

    // ======================================================================
    // UPDATE LOCAL STORE
    // ======================================================================
    const updatedData = currentData.filter(dialog => dialog.id !== id);
    dialogStore.setData(updatedData);

    console.log(`[deleteDialog] Deleted dialog: ${id}`);
    
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to delete dialog';
    console.error('[deleteDialog] Error:', err);
    
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    
    throw new Error(message);
  }
}
